import { useEffect, useState } from "react";
import { GetVersionsData } from "../../lib/data"; // You need to create this in lib/data
import Button from "../../components/GButton/Button";
import { deleteVersionsForm } from "../../lib/actions"; // You need to create this in lib/actions
import DeleteRecord from "../../ui/Models/deleteRecord";
import i18n from "../../lib/i18n";
import { FaAndroid, FaAppStoreIos, FaEdit, FaTrash, FaLink, FaCheck, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import NotData from "../../ui/sharedUi/noData";

export default function ApiVersions() {

    const [apiData, setApiData] = useState([]);
    const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, idToDelete: null });
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const t = i18n.t.bind(i18n);
    const navigate = useNavigate();

    useEffect(() => {
        handleApi();
    }, []);

    const handleApi = async () => {
        setIsLoading(true);
        // data structure expected: [{id, version, type, release_note, store_url, force, is_active}, ...]
        const data = await GetVersionsData(); 
        setApiData(data || []);
        setIsLoading(false);
    };

    const openDeleteConfirmation = (id) => {
        setDeleteConfirmation({ isOpen: true, idToDelete: id });
    };

    const closeDeleteConfirmation = () => {
        setDeleteConfirmation({ isOpen: false, idToDelete: null });
    };

    const handelDelete = async () => {
        if (deleteConfirmation.idToDelete) {
            setLoadingDelete(true);
            const result = await deleteVersionsForm(deleteConfirmation.idToDelete);
            if (result) {
                setLoadingDelete(false);
                handleApi();
            }
            closeDeleteConfirmation();
        }
    };

    return (
        <div className="p-8 sm:p-6 md:p-8 ">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-baseline bg-thirdly justify-between px-5 py-5 rounded-xl mb-6 gap-4">
                <h1 className="text-white text-lg ">{t("nav.versions") || "API Versions"}</h1>
                <Button background="#D36300" borderRadius="8px" color="white" padding="8px 20px" text={t("button.add_versions") || "Add Version"} nav={"/versions/add"} />
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {isLoading ? (
                    // Simple Skeleton
                    [1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-64 bg-thirdly animate-pulse rounded-xl"></div>
                    ))
                ) : (
                    apiData.map((item) => (
                        <div key={item.id} className="bg-thirdly rounded-xl p-5 shadow-lg relative border border-gray-700/50 hover:border-secondary transition-colors group">
                            
                            {/* Top Row: Icon & Version */}
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-3">
                                    <div className={`p-3 rounded-lg ${item.type === 'android' ? 'bg-green-900/40 text-green-400' : 'bg-blue-900/40 text-blue-400'}`}>
                                        {item.type === 'android' ? <FaAndroid size={24} /> : <FaAppStoreIos size={24} />}
                                    </div>
                                    <div>
                                        <h3 className="text-white font-bold text-xl">{item.version}</h3>
                                        <span className="text-gray-400 text-xs uppercase tracking-wide">{item.type}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {/* Action Buttons */}
                                    <button onClick={() => navigate(`/versions/edit/${item.id}`)} className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-white/10 transition">
                                        <FaEdit />
                                    </button>
                                    <button onClick={() => openDeleteConfirmation(item.id)} className="text-gray-400 hover:text-red-500 p-2 rounded-full hover:bg-white/10 transition">
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>

                            {/* Status Tags */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                <span className={`px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 ${Number(item.is_active) ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                                    {Number(item.is_active) ? <FaCheck size={10} /> : <FaTimes size={10} />} Active
                                </span>
                                <span className={`px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 ${Number(item.force) ? 'bg-orange-600/20 text-orange-400' : 'bg-gray-600/20 text-gray-400'}`}>
                                    {Number(item.force) ? "Force Update" : "Optional"}
                                </span>
                            </div>

                            {/* Release Notes Preview */}
                            <div className="mb-4">
                                <p className="text-gray-300 text-sm line-clamp-2">
                                    <span className="text-gray-500 text-xs block mb-1">EN:</span>
                                    {item.release_note?.en || item.release_note || "No notes"}
                                </p>
                            </div>

                            {/* Footer: Link */}
                            <div className="mt-auto pt-4 border-t border-gray-700">
                                <a href={item.store_url} target="_blank" rel="noreferrer" className="text-secondary  text-sm flex items-center gap-2 truncate">
                                    <FaLink /> <span className="truncate">{item.store_url || "No URL"}</span>
                                </a>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* No Data State */}
            {!isLoading && apiData.length === 0 && (
                <NotData />
            )}

            {deleteConfirmation.isOpen && <DeleteRecord loadingDelete={loadingDelete} handelDelete={handelDelete} closeDeleteConfirmation={closeDeleteConfirmation} />}
        </div>
    );
};